/**
 * Formats a job's as-advertised salary (original currency + pay period, before conversion to
 * monthly EUR) for display on the SRP job card's "original currency" toggle.
 * Symbol map intentionally duplicates the one in
 * web-app/pages/job/@jobId/@seoSlug/utils/salaryConversion.ts (getCurrencySymbol) rather than
 * importing across a page-specific directory — same acknowledged duplication as the currency
 * rate tables already duplicated between lambda/indexer.ts and lambda/utils/salaryAggregation.ts.
 */
import { formatSalaryMinMaxParts } from './format-salary-min-max-pair';

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  INR: '₹',
  BRL: 'R$',
  MXN: '$',
  CAD: 'C$',
  AUD: 'A$',
  NZD: 'NZ$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  HUF: 'Ft',
  CZK: 'Kč',
  TRY: '₺',
  ZAR: 'R',
  SGD: 'S$',
  HKD: 'HK$',
  AED: 'AED',
  SAR: 'SAR',
};

export function getOriginalCurrencySymbol(currency: string | null | undefined): string {
  if (!currency) return '€';
  return CURRENCY_SYMBOLS[currency] ?? `${currency} `;
}

/**
 * Same nowrap-per-amount split as formatSrpSalaryParts, but prefixed with the original currency
 * symbol instead of always €.
 */
export function formatOriginalSalaryParts(args: {
  min?: number | null;
  max?: number | null;
  currency?: string | null;
  locale?: string;
}): string[] {
  const min = args.min ?? undefined;
  const max = args.max ?? undefined;
  const hasMin = typeof min === 'number' && Number.isFinite(min) && min > 0;
  const hasMax = typeof max === 'number' && Number.isFinite(max) && max > 0;
  if (!hasMin || !hasMax) return [];
  const symbol = getOriginalCurrencySymbol(args.currency);
  const nf = new Intl.NumberFormat(args.locale || 'en');
  const formatAmount = (n: number) => `${symbol}${nf.format(n)}`;
  return formatSalaryMinMaxParts(min, max, formatAmount);
}
